<template>
  <v-dialog
    :model-value="modelValue"
    width="550"
    @update:model-value="onDialogVisibility"
    @click:outside="closeModal"
  >
    <v-card rounded="lg">
      <div class="bg-red-600 relative">
        <i
          class="mdi mdi-close text-xl cursor-pointer text-white absolute top-2 right-3"
          @click="closeModal"
        ></i>
        <h2 class="text-xl font-semibold text-white py-5 pl-10">
          Filter Data IMKP
        </h2>
      </div>
      <div class="px-10 py-5">
        <div class="grid grid-cols-2 gap-3">
          <div class="col-span-2">
            <FormInput
              label="Judul Proyek"
              v-model="filterLocal.judulProyek"
              placeholder="Judul proyek..."
              clearable
            />
          </div>
          <div>
            <FormInput
              label="Nama Kontraktor"
              v-model="filterLocal.namaKontraktor"
              placeholder="Nama kontraktor..."
              clearable
            />
          </div>
          <div>
            <FormOption
              label="Status"
              v-model="filterLocal.status"
              :items="statusOptions"
              item-value="status"
              item-title="name"
              clearable
              placeholder="Semua status"
            />
          </div>
          <div class="col-span-2">
            <p class="text-xs font-medium text-gray-600 mb-1">Tanggal</p>
            <div class="grid grid-cols-2 gap-3">
              <FormDate
                v-model="filterLocal.tanggalDari"
                placeholder="Pilih tanggal mulai"
                label="Tanggal Mulai"
                :max="filterLocal.tanggalSampai"
                :rules="rulesTanggalDari"
                clearable
              />
              <FormDate
                v-model="filterLocal.tanggalSampai"
                placeholder="Pilih tanggal selesai"
                label="Tanggal Selesai"
                :min="filterLocal.tanggalDari"
                :rules="rulesTanggalSampai"
                clearable
              />
            </div>
          </div>
          <div class="col-span-2 flex flex-col gap-2">
            <FormButton
              label="Terapkan"
              classes="w-full bg-red-600 text-white capitalize"
              @click="applyFilter"
            />
            <FormButton
              label="Refresh Filter"
              classes="w-full capitalize"
              variant="outlined"
              color="#dc2626"
              @click="refreshFilter"
            />
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import FormInput from "@/components/FormInput.vue";
import FormOption from "@/components/FormOption.vue";
import FormButton from "@/components/FormButton.vue";
import FormDate from "@/components/FormDate.vue";
import { cloneDeep } from "lodash";
import util from "@/assets/js/util";

const EMPTY_FILTER = {
  judulProyek: null,
  namaKontraktor: null,
  status: null,
  tanggalDari: null,
  tanggalSampai: null,
};

export default {
  name: "FilterImkpModal",
  components: {
    FormInput,
    FormOption,
    FormButton,
    FormDate,
  },
  emits: ["update:modelValue", "update:filter", "apply", "refresh"],
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    filter: {
      type: Object,
      default: () => ({}),
    },
    listStatus: {
      type: Array,
      default: () => [],
    },
    role: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      filterLocal: cloneDeep(EMPTY_FILTER),
    };
  },
  computed: {
    statusOptions() {
      if (this.role == null) return this.listStatus;
      return this.listStatus.filter((status) =>
        status.role.some((level) => level === this.role),
      );
    },
    rulesTanggalDari() {
      return [
        (v) =>
          !v ||
          !this.filterLocal.tanggalSampai ||
          !this.isStartAfterEnd(v, this.filterLocal.tanggalSampai) ||
          "Tanggal mulai tidak boleh lebih dari tanggal selesai",
      ];
    },
    rulesTanggalSampai() {
      return [
        (v) =>
          !v ||
          !this.filterLocal.tanggalDari ||
          !this.isStartAfterEnd(this.filterLocal.tanggalDari, v) ||
          "Tanggal selesai tidak boleh kurang dari tanggal mulai",
      ];
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.syncFromProp();
      }
    },
  },
  methods: {
    toComparableDate(value) {
      if (!value) return null;
      const date = value instanceof Date ? new Date(value) : new Date(value);
      if (Number.isNaN(date.getTime())) return null;
      date.setHours(0, 0, 0, 0);
      return date;
    },
    isStartAfterEnd(start, end) {
      const from = this.toComparableDate(start);
      const to = this.toComparableDate(end);
      if (!from || !to) return false;
      return from.getTime() > to.getTime();
    },
    isDateRangeInvalid() {
      const { tanggalDari, tanggalSampai } = this.filterLocal;
      if (!tanggalDari || !tanggalSampai) return false;
      return this.isStartAfterEnd(tanggalDari, tanggalSampai);
    },
    syncFromProp() {
      this.filterLocal = {
        ...cloneDeep(EMPTY_FILTER),
        judulProyek: this.filter.judulProyek ?? null,
        namaKontraktor: this.filter.namaKontraktor ?? null,
        status: this.filter.status ?? null,
        tanggalDari: this.filter.tanggalDari ?? null,
        tanggalSampai: this.filter.tanggalSampai ?? null,
      };
    },
    emitFilter() {
      this.$emit("update:filter", cloneDeep(this.filterLocal));
    },
    applyFilter() {
      if (this.isDateRangeInvalid()) {
        util.showError("Tanggal mulai tidak boleh lebih dari tanggal selesai");
        return;
      }
      this.emitFilter();
      this.$emit("apply");
      this.closeModal();
    },
    refreshFilter() {
      this.filterLocal = cloneDeep(EMPTY_FILTER);
      this.emitFilter();
      this.$emit("refresh");
    },
    closeModal() {
      this.$emit("update:modelValue", false);
    },
    onDialogVisibility(open) {
      this.$emit("update:modelValue", open);
    },
  },
};
</script>
